import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { 
  FaCalendarAlt, 
  FaUsers, 
  FaClock, 
  FaEye, 
  FaEyeSlash, 
  FaFilter, 
  FaSearch, 
  FaDownload, 
  FaSpinner, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaUser,
  FaUserTie,
  FaUtensils,
  FaGlassWhiskey,
  FaChartBar,
  FaCalendarWeek,
  FaCalendarDay,
  FaTimes,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import EmployeeCard from './EmployeeCard';
import SchedulePopup from './SchedulePopup';
import './ManageSchedule.css';

const ManageSchedule = () => {
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [todaysWorkers, setTodaysWorkers] = useState([]);
  const [employeeColors, setEmployeeColors] = useState({});
  const [hiddenEmployees, setHiddenEmployees] = useState(new Set());
  const [selectedDay, setSelectedDay] = useState(null);
  const [hiddenRoles, setHiddenRoles] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'grid'
  const [showLegend, setShowLegend] = useState(true);

  // Récupérer les employés (tous sauf ceux ayant le rôle "admin")
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        setError(null);
        
      const employeesCollection = collection(db, 'identifications');
      const q = query(employeesCollection, where('role', '!=', 'admin'));
      const snapshot = await getDocs(q);
      const employeesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEmployees(employeesList);

      // Assignation d'une couleur unique à chaque employé
      const colors = employeesList.reduce((acc, employee, index) => {
        acc[employee.id] = generateUniqueColor(index);
        return acc;
      }, {});
      setEmployeeColors(colors);
        
        setSuccessMessage('Employés chargés avec succès');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Erreur lors du chargement des employés');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Générer une couleur unique (par exemple, à partir d'un index)
  const generateUniqueColor = (index) => {
    const colors = [
      '#FF6347', '#4682B4', '#32CD32', '#FFD700', '#8A2BE2', '#DC143C',
      '#A52A2A', '#5F9EA0', '#D2691E', '#FF4500', '#00BFFF', '#FF1493', '#00FA9A',
      '#F4A300', '#3CB371', '#7B68EE'
    ];
    return colors[index % colors.length]; // Retourner une couleur unique par employé
  };

  // Récupérer les horaires des employés
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
      const schedulesCollection = collection(db, 'horaires');
      const snapshot = await getDocs(schedulesCollection);
      const schedulesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      const schedulesData = schedulesList.reduce((acc, schedule) => {
        acc[schedule.employeeId] = schedule;
        return acc;
      }, {});

      setSchedules(schedulesData);
      } catch (err) {
        setError('Erreur lors du chargement des horaires');
        console.error('Erreur:', err);
      }
    };

    fetchSchedules();
  }, []);

  // Fonction pour obtenir les employés qui travaillent aujourd'hui
  useEffect(() => {
    const today = new Date();
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const todayDayName = dayNames[today.getDay() - 1]; // "getDay()" retourne un nombre de 0 (Dimanche) à 6 (Samedi)

    const workersToday = employees.filter(employee => {
      const schedule = schedules[employee.id];
      return schedule && schedule.schedule[todayDayName] && schedule.schedule[todayDayName] !== 'Ne travaille pas';
    });

    setTodaysWorkers(workersToday);
  }, [employees, schedules]);

  const handleEmployeeClick = (employee, day) => {
    setSelectedEmployee(employee);
    setSelectedDay(day);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedEmployee(null);
  };

  // Mettre à jour l'état des horaires après modification
  const updateSchedule = (employeeId, newSchedule) => {
    setSchedules(prevSchedules => ({
      ...prevSchedules,
      [employeeId]: { ...newSchedule },
    }));
  };

  // Fonction pour afficher le sélecteur de jour
  const renderDaySelector = () => {
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayNamesFrench = {
      'Monday': 'Lundi',
      'Tuesday': 'Mardi', 
      'Wednesday': 'Mercredi',
      'Thursday': 'Jeudi',
      'Friday': 'Vendredi',
      'Saturday': 'Samedi',
      'Sunday': 'Dimanche'
    };

    return (
      <div className="schedule-management-day-selector">
        <div className="schedule-management-day-selector-header">
          <FaCalendarWeek className="schedule-management-day-selector-icon" />
          <h3>Choisir un jour</h3>
        </div>
        <div className="schedule-management-day-buttons">
          {daysOfWeek.map((day) => (
            <button
              key={day}
              className={`schedule-management-day-btn ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(selectedDay === day ? null : day)}
            >
              <span className="schedule-management-day-btn-text">
                {dayNamesFrench[day]}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Fonction pour afficher le détail d'un jour
  const renderDayDetail = () => {
    if (!selectedDay) return null;

    const dayNamesFrench = {
      'Monday': 'Lundi',
      'Tuesday': 'Mardi', 
      'Wednesday': 'Mercredi',
      'Thursday': 'Jeudi',
      'Friday': 'Vendredi',
      'Saturday': 'Samedi',
      'Sunday': 'Dimanche'
    };
    
    // Créer des tranches de 30 minutes de 10h à 22h30
    const timeSlots = Array.from({ length: 26 }, (_, i) => {
      const hour = Math.floor(i / 2) + 10;
      const minutes = i % 2 === 0 ? '00' : '30';
      return `${hour}:${minutes}`;
    });

    // Créer la grille pour le jour sélectionné
    const daySchedule = timeSlots.map((timeSlot) => {
      const [hour, minutes] = timeSlot.split(':').map(Number);
      const workersForSlot = employees
        .filter(employee => !hiddenEmployees.has(employee.id))
        .filter(employee => !hiddenRoles.has(employee.role))
        .filter(employee => {
          const schedule = schedules[employee.id];
          const workTime = schedule?.schedule[selectedDay] || '';
          return isWorking(workTime, hour, minutes);
        });
      return workersForSlot;
    });

    return (
      <div className="schedule-management-day-detail">
        <div className="schedule-management-day-detail-header">
          <FaCalendarDay className="schedule-management-day-detail-icon" />
          <h3>Planning du {dayNamesFrench[selectedDay]}</h3>
          <button 
            className="schedule-management-close-day-btn"
            onClick={() => setSelectedDay(null)}
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="schedule-management-day-table">
          <div className="schedule-management-day-table-header">
            <div className="schedule-management-day-table-time-header">Heure</div>
            <div className="schedule-management-day-table-employees-header">Employés présents</div>
          </div>
          
          {timeSlots.map((timeSlot, slotIndex) => {
            const employeesInSlot = daySchedule[slotIndex];
            const hasEmployees = employeesInSlot.length > 0;
            
            return (
              <div key={slotIndex} className={`schedule-management-day-table-row ${hasEmployees ? 'has-employees' : ''}`}>
                <div className="schedule-management-day-table-time-cell">
                  {timeSlot}
                </div>
                <div className="schedule-management-day-table-employees-cell">
                  {hasEmployees ? (
                    <div className="schedule-management-day-employees-list">
                      {employeesInSlot.map((employee) => {
                        const color = employeeColors[employee.id];
                        const RoleIcon = getRoleIcon(employee.role);
                        return (
                          <div 
                            key={employee.id} 
                            className="schedule-management-day-employee-badge"
                            style={{ backgroundColor: color }}
                          >
                            <RoleIcon className="schedule-management-day-employee-badge-icon" />
                            <span className="schedule-management-day-employee-badge-name">
                              {employee.firstName}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="schedule-management-day-no-employees">Aucun employé</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Fonction pour afficher le planning sous forme de tableau
  const renderScheduleGrid = () => {
    if (viewMode !== 'grid') return null;

    if (selectedDay) {
      return renderDayDetail();
    }
    
    return renderDaySelector();
  };

  const handleRoleToggle = (role) => {
    const newHiddenRoles = new Set(hiddenRoles);
    
    if (hiddenRoles.has(role)) {
      // Si on réactive le rôle
      newHiddenRoles.delete(role);
      // Réactiver tous les employés de ce rôle qui étaient cachés à cause du rôle
      const newHiddenEmployees = new Set(hiddenEmployees);
      employees.forEach(employee => {
        if (employee.role === role) {
          newHiddenEmployees.delete(employee.id);
        }
      });
      setHiddenEmployees(newHiddenEmployees);
    } else {
      // Si on désactive le rôle
      newHiddenRoles.add(role);
      // Désactiver tous les employés de ce rôle
      const newHiddenEmployees = new Set(hiddenEmployees);
      employees.forEach(employee => {
        if (employee.role === role) {
          newHiddenEmployees.add(employee.id);
        }
      });
      setHiddenEmployees(newHiddenEmployees);
    }
    
    setHiddenRoles(newHiddenRoles);
  };

  const handleEmployeeToggle = (employeeId) => {
    const newHiddenEmployees = new Set(hiddenEmployees);
    const employee = employees.find(emp => emp.id === employeeId);
    
    if (hiddenEmployees.has(employeeId)) {
      // Réactiver l'employé
      newHiddenEmployees.delete(employeeId);
      
      // Vérifier si on doit réactiver le rôle
      if (hiddenRoles.has(employee.role)) {
        const roleEmployees = employees.filter(emp => emp.role === employee.role);
        const otherHiddenEmployeesCount = roleEmployees.filter(emp => 
          emp.id !== employeeId && newHiddenEmployees.has(emp.id)
        ).length;
        
        // Si tous les autres employés du rôle sont visibles, réactiver le rôle
        if (otherHiddenEmployeesCount === 0) {
          setHiddenRoles(prev => {
            const newHiddenRoles = new Set(prev);
            newHiddenRoles.delete(employee.role);
            return newHiddenRoles;
          });
        }
      }
    } else {
      // Désactiver l'employé
      newHiddenEmployees.add(employeeId);
      
      // Vérifier si tous les employés du même rôle sont désactivés
      const roleEmployees = employees.filter(emp => emp.role === employee.role);
      const allRoleEmployeesHidden = roleEmployees.every(emp => 
        emp.id === employeeId || newHiddenEmployees.has(emp.id)
      );
      
      // Si tous les employés du rôle sont désactivés, désactiver le rôle
      if (allRoleEmployeesHidden) {
        setHiddenRoles(prev => {
          const newHiddenRoles = new Set(prev);
          newHiddenRoles.add(employee.role);
          return newHiddenRoles;
        });
      }
    }
    
    setHiddenEmployees(newHiddenEmployees);
  };

  // Légende des couleurs
  const renderLegend = () => {
    return (
      <div className="schedule-management-legend">
        <div className="schedule-management-legend-header">
          <FaEye className="schedule-management-legend-icon" />
          <h4 className="schedule-management-legend-title">Légende</h4>
        </div>
        
        <div className="schedule-management-legend-sections">
          <div className="schedule-management-legend-section">
            <h5 className="schedule-management-legend-section-title">
              <FaUsers className="schedule-management-legend-section-icon" />
              Employés
            </h5>
            <div className="schedule-management-legend-items">
              {employees.map(employee => {
                const isRoleHidden = hiddenRoles.has(employee.role);
                const isEmployeeHidden = hiddenEmployees.has(employee.id);
                const RoleIcon = getRoleIcon(employee.role);
                
                return (
                  <div
                    key={employee.id}
                    className={`schedule-management-legend-item ${(isRoleHidden || isEmployeeHidden) ? 'disabled' : ''}`}
                    onClick={() => handleEmployeeToggle(employee.id)}
                  >
                    <div 
                      className="schedule-management-legend-color" 
                      style={{ backgroundColor: employeeColors[employee.id] }}
                    ></div>
                    <RoleIcon className="schedule-management-legend-role-icon" />
                    <span className="schedule-management-legend-text">
                      {employee.firstName} {employee.lastName}
                    </span>
                    <div className="schedule-management-legend-toggle">
                      {(isRoleHidden || isEmployeeHidden) ? <FaToggleOff /> : <FaToggleOn />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="schedule-management-legend-section">
            <h5 className="schedule-management-legend-section-title">
              <FaUserTie className="schedule-management-legend-section-icon" />
              Rôles
            </h5>
            <div className="schedule-management-legend-items">
              {uniqueRoles.map(role => {
                const RoleIcon = getRoleIcon(role);
                return (
                  <div
                    key={role}
                    className={`schedule-management-legend-item ${hiddenRoles.has(role) ? 'disabled' : ''}`}
                    onClick={() => handleRoleToggle(role)}
                  >
                    <div 
                      className="schedule-management-legend-role-indicator"
                      style={{ backgroundColor: employeeColors[role] }}
                    ></div>
                    <RoleIcon className="schedule-management-legend-role-icon" />
                    <span className="schedule-management-legend-text">
                      {getRoleInFrench(role)}
                    </span>
                    <div className="schedule-management-legend-toggle">
                      {hiddenRoles.has(role) ? <FaToggleOff /> : <FaToggleOn />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isWorking = (workTime, hour, minutes) => {
    if (!workTime || workTime === 'Ne travaille pas') {
      return false;
    }

    return workTime.split(' / ').some(period => {
      const [start, end] = period.split(' - ').map(time => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + (m || 0); // Convertir en minutes
      });
      const currentTime = hour * 60 + minutes;
      return start <= currentTime && currentTime < end;
    });
  };

  const renderTodaysWorkers = () => {
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayDayName = dayNames[today.getDay()];

    return (
      <div className="schedule-management-todays-workers">
        <div className="schedule-management-todays-header">
          <FaClock className="schedule-management-todays-icon" />
          <h3 className="schedule-management-todays-title">
            Personnel travaillant aujourd'hui ({new Date().toLocaleDateString('fr-FR', { weekday: 'long' })})
          </h3>
        </div>
        <div className="schedule-management-workers-grid">
          {todaysWorkers.map((worker) => {
            const RoleIcon = getRoleIcon(worker.role);
            return (
              <div key={worker.id} className="schedule-management-worker-card">
                <div className="schedule-management-worker-header">
                  <div className="schedule-management-worker-avatar">
                    <RoleIcon />
                  </div>
                  <div className="schedule-management-worker-info">
                    <div className="schedule-management-worker-name">
                      {worker.firstName} {worker.lastName}
                    </div>
                    <div className="schedule-management-worker-role">
                      {getRoleInFrench(worker.role)}
                    </div>
                  </div>
                </div>
                <div className="schedule-management-worker-schedule">
                    {schedules[worker.id]?.schedule[todayDayName] === 'Ne travaille pas' 
                      ? 'Repos' 
                      : schedules[worker.id]?.schedule[todayDayName]}
                  </div>
                </div>
            );
          })}
          {todaysWorkers.length === 0 && (
            <div className="schedule-management-no-workers">
              <FaUsers className="schedule-management-no-workers-icon" />
              <p className="schedule-management-no-workers-text">
                Aucun personnel prévu aujourd'hui
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getRoleInFrench = (role) => {
    const roles = {
      server: 'Serveur',
      cook: 'Cuisinier',
      bar: 'Barman',
      admin: 'Administrateur'
    };
    return roles[role] || role;
  };

  const getRoleIcon = (role) => {
    const icons = {
      server: FaUserTie,
      cook: FaUtensils,
      bar: FaGlassWhiskey,
      admin: FaUser
    };
    return icons[role] || FaUser;
  };

  // Filtrer les employés selon les critères
  const filteredEmployees = useCallback(() => {
    return employees.filter(employee => {
      const matchesSearch = searchTerm === '' || 
        `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === 'all' || employee.role === selectedRole;
      const isVisible = !hiddenEmployees.has(employee.id) && !hiddenRoles.has(employee.role);
      
      return matchesSearch && matchesRole && isVisible;
    });
  }, [employees, searchTerm, selectedRole, hiddenEmployees, hiddenRoles]);

  // Obtenir les rôles uniques
  const uniqueRoles = [...new Set(employees.map(emp => emp.role))];

  // Fonction pour exporter le planning
  const exportSchedule = () => {
    const data = {
      employees: filteredEmployees(),
      schedules: schedules,
      exportDate: new Date().toISOString(),
      totalEmployees: employees.length,
      workingToday: todaysWorkers.length
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planning-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="schedule-management-loading-container">
        <div className="schedule-management-loading-spinner">
          <FaSpinner className="spinning" />
        </div>
        <p className="schedule-management-loading-text">Chargement du planning...</p>
      </div>
    );
  }

  return (
    <div className="schedule-management-container">
      {/* Header */}
      <div className="schedule-management-header">
        <div className="schedule-management-title-section">
          <FaCalendarAlt className="schedule-management-title-icon" />
          <h1 className="schedule-management-title">Gestion du Planning</h1>
        </div>
        <p className="schedule-management-subtitle">
          Gérez les horaires et le planning de votre équipe
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="schedule-management-error-message">
          <FaExclamationTriangle className="schedule-management-message-icon" />
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="schedule-management-success-message">
          <FaCheckCircle className="schedule-management-message-icon" />
          {successMessage}
        </div>
      )}

      {/* Controls */}
      <div className="schedule-management-controls">
        <div className="schedule-management-search-section">
          <div className="schedule-management-search-container">
            <FaSearch className="schedule-management-search-icon" />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="schedule-management-search-input"
            />
          </div>
        </div>

        <div className="schedule-management-filters">
          <div className="schedule-management-filter-group">
            <FaFilter className="schedule-management-filter-icon" />
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="schedule-management-role-select"
            >
              <option value="all">Tous les rôles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>
                  {getRoleInFrench(role)}
                </option>
              ))}
            </select>
          </div>

          <div className="schedule-management-view-controls">
            <button
              className={`schedule-management-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              <FaUsers />
              <span>Cartes</span>
            </button>
            <button
              className={`schedule-management-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FaChartBar />
              <span>Grille</span>
            </button>
          </div>

          <div className="schedule-management-actions">
            <button
              className="schedule-management-legend-toggle"
              onClick={() => setShowLegend(!showLegend)}
            >
              {showLegend ? <FaEyeSlash /> : <FaEye />}
              <span>Légende</span>
            </button>
            <button
              className="schedule-management-export-btn"
              onClick={exportSchedule}
            >
              <FaDownload />
              <span>Exporter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Today's Workers */}
      {renderTodaysWorkers()}

      {/* Main Content */}
      <div className="schedule-management-content">
        {viewMode === 'cards' ? (
          <div className="schedule-management-employee-cards">
            {filteredEmployees().map(employee => (
          <EmployeeCard
            key={employee.id}
            employee={employee}
            onClick={(employee, day) => handleEmployeeClick(employee, day)}
            schedule={schedules[employee.id]}
          />
        ))}
      </div>
        ) : (
          <div className="schedule-management-grid-section">
      {renderScheduleGrid()}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="schedule-management-legend-section">
      {renderLegend()}
        </div>
      )}

      {/* Popup */}
      {showPopup && (
          <SchedulePopup
            employee={selectedEmployee}
            onClose={handleClosePopup}
            existingSchedule={schedules[selectedEmployee.id]}
            updateSchedule={updateSchedule}
            selectedDay={selectedDay}
          />
      )}
    </div>
  );
};

export default ManageSchedule;
